import React, { useState, useEffect } from 'react';
import { MessageSquare, Plus, Search, ArrowLeft } from 'lucide-react';
import { useClientMessaging } from '../contexts/ClientMessagingContext';
import { getEligibleIntervenants } from '../services/eligibleIntervenants';
import ConversationList from './ConversationList';
import ChatInterface from './ChatInterface';
import NewConversationModal from './NewConversationModal';
import { useTheme } from '../contexts/ThemeContext';

const ClientMessaging = ({ db, client }) => {
  const {
    conversations,
    activeConversation,
    unreadCounts,
    loading,
    getFilteredConversations,
    setActiveConversation
  } = useClientMessaging();

  const { theme } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);
  const [eligibleIntervenants, setEligibleIntervenants] = useState([]);
  const [loadingIntervenants, setLoadingIntervenants] = useState(true);

  // Détection du mode mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileView(mobile);
      if (mobile) {
        setShowConversationList(!activeConversation);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [activeConversation]);

  // Récupérer les intervenants éligibles
  useEffect(() => {
    const fetchEligibleIntervenants = async () => {
      if (!db || !client?.id) return;

      setLoadingIntervenants(true);
      try {
        const intervenants = await getEligibleIntervenants(db, client.id);
        setEligibleIntervenants(intervenants);
        console.log(`✅ ${intervenants.length} intervenants éligibles chargés`);
      } catch (error) {
        console.error('❌ Erreur chargement intervenants éligibles:', error);
        setEligibleIntervenants([]);
      } finally {
        setLoadingIntervenants(false);
      }
    };

    fetchEligibleIntervenants();
  }, [db, client?.id]);

  // Gérer le changement de conversation sur mobile
  useEffect(() => {
    if (isMobileView && activeConversation) {
      setShowConversationList(false);
    }
  }, [activeConversation, isMobileView]);

  // Navigation vers la liste des conversations
  const handleBackToList = () => {
    setActiveConversation(null);
    setShowConversationList(true);
  };

  // Navigation vers une conversation
  const handleSelectConversation = (conversation) => {
    if (isMobileView) {
      setShowConversationList(false);
    }
  };

  // Filtrer les conversations (seulement les conversations clients)
  const filteredConversations = getFilteredConversations('client', false).filter(conv => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return conv.lastMessage?.toLowerCase().includes(searchLower) ||
      conv.participantNames?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="client-messaging-container">
      {isMobileView ? (
        <>
          {/* Vue mobile */}
          {showConversationList ? (
            <div className="mobile-conversation-list">
              <div className="mobile-header">
                <div className="mobile-header-title">
                  <MessageSquare size={24} />
                  <h1>Messagerie</h1>
                </div>
                <div className="mobile-header-actions">
                  {unreadCounts.client > 0 && (
                    <span className="badge badge-client">
                      {unreadCounts.client}
                    </span>
                  )}
                  {eligibleIntervenants.length > 0 && (
                    <button
                      className="mobile-new-conversation"
                      onClick={() => setShowNewConversation(true)}
                      disabled={loadingIntervenants}
                    >
                      <Plus size={20} />
                    </button>
                  )}
                </div>
              </div>
              <div className="mobile-search-container">
                <Search size={16} className="mobile-search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mobile-search-input"
                />
              </div>
              <div className="mobile-conversation-list-container">
                {loading ? (
                  <div className="loading-container">
                    <div>Chargement des conversations...</div>
                  </div>
                ) : (
                  <ConversationList
                    conversations={filteredConversations}
                    loading={false}
                    currentUser={{ userId: client.id }}
                    activeTab="client"
                    db={db}
                    onSelectConversation={handleSelectConversation}
                    isClientView={true}
                  />
                )}
              </div>
            </div>
          ) : activeConversation && (
            <div className="mobile-chat-view">
              <div className="mobile-chat-header">
                <button
                  className="mobile-back-button"
                  onClick={handleBackToList}
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="mobile-chat-title">
                  <h2>{activeConversation.groupName || activeConversation.participantNames || 'Conversation'}</h2>
                </div>
              </div>
              <div className="mobile-chat-container">
                <ChatInterface
                  currentUser={{ userId: client.id }}
                  canWrite={true}
                  db={db}
                  isMobile={true}
                  isClientView={true}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Vue desktop */}
          <div className="messaging-header">
            <div className="messaging-title">
              <MessageSquare size={24} />
              <h1>Messagerie</h1>
            </div>
            {unreadCounts.client > 0 && (
              <span className="badge badge-client">
                {unreadCounts.client}
              </span>
            )}
            {eligibleIntervenants.length > 0 && (
              <button
                className="btn-new-conversation"
                onClick={() => setShowNewConversation(true)}
                disabled={loadingIntervenants}
              >
                <Plus size={16} />
                Nouvelle conversation
              </button>
            )}
          </div>
          <div className="messaging-content">
            <div className="conversations-sidebar">
              <div className="search-container">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="conversation-list-container">
                {loading ? (
                  <div className="loading-container">
                    <div>Chargement des conversations...</div>
                  </div>
                ) : (
                  <ConversationList
                    conversations={filteredConversations}
                    loading={false}
                    currentUser={{ userId: client.id }}
                    activeTab="client"
                    db={db}
                    isClientView={true}
                  />
                )}
              </div>
            </div>
            <div className="chat-container">
              {activeConversation ? (
                <ChatInterface
                  currentUser={{ userId: client.id }}
                  canWrite={true}
                  db={db}
                  isClientView={true}
                />
              ) : (
                <div className="chat-placeholder">
                  <MessageSquare size={48} className="placeholder-icon" />
                  <h3>Sélectionnez une conversation</h3>
                  <p>Choisissez une conversation dans la liste pour commencer à discuter.</p>
                  {eligibleIntervenants.length > 0 && (
                    <button
                      className="btn-start-conversation"
                      onClick={() => setShowNewConversation(true)}
                    >
                      <Plus size={16} />
                      Démarrer une conversation
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modal nouvelle conversation - Commun aux deux vues */}
      {showNewConversation && (
        <NewConversationModal
          onClose={() => setShowNewConversation(false)}
          currentUser={{ userId: client.id }}
          db={db}
          eligibleIntervenants={eligibleIntervenants}
          isClientView={true}
        />
      )}
    </div>
  );
};

export default ClientMessaging;
